/**
 * CampusOverview — AOS-CAMPUS-DEMO-02 Phase 3
 *
 * Route: /campus/overview
 * Module: Domain 5 "Campus Services"
 *
 * Displays the 11 canonical Campus Services modules with accurate status:
 *   - Hostels & Residential Life (08.02): VERIFIED — navigates to /campus/hostel
 *   - All other 9 operational services: COMING SOON — non-interactive
 *
 * Governance: No fabricated KPIs. No mock integrations.
 * Status labels derived from actual module entitlement (useTenantModules).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BedDouble,
  Building2,
  Layers,
  Route,
  ShoppingCart,
  BookOpen,
  Activity,
  Users2,
  CalendarClock,
  HeartHandshake,
  ShieldCheck,
  Users,
  ArrowRight,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { PageHeader } from "../components/layout/Shell";
import { useTenantModules } from "../lib/useTenantModules";

const CAMPUS_MODULES = [
  {
    code: "08.02",
    label: "Hostels & Residential Life",
    description:
      "Room catalogue, availability search, booking management, and residential finance invoicing for VCE's Kaveri Bhavan.",
    icon: BedDouble,
    route: "/campus/hostel",
    testid: "campus-tile-hostel",
  },
  {
    code: null,
    label: "Facilities & Maintenance",
    description:
      "Asset registers, preventive maintenance scheduling, fault reporting and work-order tracking.",
    icon: Building2,
    route: null,
    testid: "campus-tile-facilities",
  },
  {
    code: null,
    label: "Space & Asset Booking",
    description:
      "Self-service booking for classrooms, labs, seminar halls, sports facilities and shared equipment.",
    icon: Layers,
    route: null,
    testid: "campus-tile-space-booking",
  },
  {
    code: null,
    label: "Transport & Parking",
    description:
      "Bus-route management, pass issuance, vehicle tracking and campus parking allocation.",
    icon: Route,
    route: null,
    testid: "campus-tile-transport",
  },
  {
    code: null,
    label: "Dining & Meal Plans",
    description:
      "Canteen management, meal-plan subscription, menu publishing and nutritional tracking.",
    icon: ShoppingCart,
    route: null,
    testid: "campus-tile-dining",
  },
  {
    code: null,
    label: "Library Services",
    description:
      "Catalogue search, issue/return, fine management, digital resource access and seat reservations.",
    icon: BookOpen,
    route: null,
    testid: "campus-tile-library",
  },
  {
    code: null,
    label: "Sports & Recreation",
    description:
      "Sports facility bookings, team registrations, inter-collegiate tournament and fitness tracking.",
    icon: Activity,
    route: null,
    testid: "campus-tile-sports",
  },
  {
    code: null,
    label: "Clubs & Student Activities",
    description:
      "Club registration, event proposals, budget requests and student activity reporting.",
    icon: Users2,
    route: null,
    testid: "campus-tile-clubs",
  },
  {
    code: null,
    label: "Campus Events",
    description:
      "Institutional event calendar, venue booking, attendee management and communication.",
    icon: CalendarClock,
    route: null,
    testid: "campus-tile-events",
  },
  {
    code: null,
    label: "Health & Wellbeing",
    description:
      "Campus clinic appointments, health-record management, wellness programmes and emergency protocols.",
    icon: HeartHandshake,
    route: null,
    testid: "campus-tile-health",
  },
  {
    code: "08.03",
    label: "Safety, Security & Access",
    description:
      "Visitor management, incident reporting, energy tracking and campus access control.",
    icon: ShieldCheck,
    route: "/safe",
    testid: "campus-tile-safe",
  },
];

export default function CampusOverview() {
  const navigate = useNavigate();
  const { statusOf } = useTenantModules();

  const getModuleStatus = (mod) => {
    if (!mod.code && !mod.route) return "coming_soon";
    if (mod.code) {
      const s = statusOf(mod.code);
      return s === "active" ? "active" : "coming_soon";
    }
    // has route but no module code (legacy/safe page)
    return "active";
  };

  const handleTileClick = (mod) => {
    const status = getModuleStatus(mod);
    if (status === "active" && mod.route) {
      navigate(mod.route);
    }
  };

  const activeTiles = CAMPUS_MODULES.filter(
    (m) => getModuleStatus(m) === "active"
  );
  const comingSoonTiles = CAMPUS_MODULES.filter(
    (m) => getModuleStatus(m) === "coming_soon"
  );

  return (
    <>
      <PageHeader
        eyebrow="Campus Services"
        title="Campus Overview"
        description="All campus operational services for your institution. Available services are highlighted."
      />
      <div className="p-4 md:p-8 space-y-8">
        {/* ── Available Services ─────────────────────────────────────────── */}
        {activeTiles.length > 0 && (
          <section data-testid="campus-overview-available-section">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Available ({activeTiles.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeTiles.map((mod) => (
                <ModuleTile
                  key={mod.testid}
                  mod={mod}
                  status="active"
                  onClick={() => handleTileClick(mod)}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Coming Soon Services ───────────────────────────────────────── */}
        {comingSoonTiles.length > 0 && (
          <section data-testid="campus-overview-coming-soon-section">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Coming Soon ({comingSoonTiles.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {comingSoonTiles.map((mod) => (
                <ModuleTile
                  key={mod.testid}
                  mod={mod}
                  status="coming_soon"
                  onClick={null}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function ModuleTile({ mod, status, onClick }) {
  const Icon = mod.icon;
  const isActive = status === "active";

  return (
    <div
      data-testid={mod.testid}
      onClick={isActive ? onClick : undefined}
      className={[
        "relative flex flex-col gap-3 rounded-xl border p-5 transition-all",
        isActive
          ? "cursor-pointer border-border bg-card hover:border-primary/50 hover:shadow-md hover:bg-accent/30"
          : "cursor-not-allowed border-border/50 bg-muted/30 opacity-60",
      ].join(" ")}
    >
      {/* Status badge */}
      <div className="absolute top-3 right-3">
        {isActive ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-500/20"
            data-testid={`${mod.testid}-badge`}
          >
            <CheckCircle2 className="h-3 w-3" />
            Available
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border"
            data-testid={`${mod.testid}-badge`}
          >
            <Clock className="h-3 w-3" />
            Coming Soon
          </span>
        )}
      </div>

      {/* Icon */}
      <div
        className={[
          "flex h-10 w-10 items-center justify-center rounded-lg",
          isActive
            ? "bg-primary/10 text-primary"
            : "bg-muted text-muted-foreground",
        ].join(" ")}
      >
        <Icon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        <h3
          className={[
            "text-sm font-semibold leading-tight",
            isActive ? "text-foreground" : "text-muted-foreground",
          ].join(" ")}
        >
          {mod.label}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
          {mod.description}
        </p>
      </div>

      {/* Arrow for active tiles */}
      {isActive && (
        <div className="flex items-center gap-1 text-xs font-medium text-primary">
          Open
          <ArrowRight className="h-3 w-3" />
        </div>
      )}
    </div>
  );
}
